import React, { memo, useRef } from "react";

import { PieChart, Pie, Cell, ResponsiveContainer, Label } from "recharts";

import Card from "../../../components/Card";
import LoadingElevaty from "../../../components/LoadingElevaty";
import { normalizeBoolean, useClient } from "../../../hooks/ClientProvider";
import designSystemColors from "../../../styles";
import * as S from "../style";
import LimitInformationContainer from "./LimitInformationContainer";
import { Tabs } from "../../../components-v2/Tabs";

function CustomLabel({ viewBox, value }) {
  const { cx, cy } = viewBox;
  const formattedValue = value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (
    <g>
      <text
        x={cx}
        y={cy - 25}
        className="total"
        textAnchor="middle"
        dominantBaseline="central"
      >
        TOTAL R$
      </text>
      <text
        x={cx}
        y={cy + 5}
        className="amount"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {formattedValue}
      </text>
    </g>
  );
}

function CustomLegend({ payload }) {
  return (
    <ul className="CustomLegendLimits">
      {payload.map((entry) => (
        <li
          key={entry.name}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginBottom: "25px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg height="20" width="20" style={{ marginRight: "10px" }}>
              <circle cx="10" cy="10" r="10" fill={entry.color} />
            </svg>
            <span style={{ fontWeight: "bold" }}>{entry.value}</span>
          </div>
          <p style={{ color: entry.color }}>
            {`R$ ${entry.payload.value.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </p>
        </li>
      ))}
    </ul>
  );
}

function CardsLimitChart() {
  const ref = useRef();
  const {
    client,
    cardFinancial,
    isLoadingCardFinancial,
    isLoadingClient,
  } = useClient();

  const limitePrincipal = cardFinancial?.limitePrincipal ?? 0;
  const limiteExtra = Number(client?.client?.limiteExtra) || 0;
  const saldoDisponivelExtra = Number(client?.client?.saldoDisponivelExtra) || 0;
  const utilizaLimiteExtra = normalizeBoolean(client?.client?.utilizaLimiteExtra);
  
  const total = utilizaLimiteExtra
    ? limitePrincipal + limiteExtra
    : limitePrincipal;
    
  const saldoDisponivel = cardFinancial?.saldoDisponivel ?? client?.client?.saldoDisponivel ?? 0;
  const limiteExtraTotal = client?.client?.limiteExtraTotal ?? 0;
  const devedorTotal = client?.client?.devedorTotal ?? 0;

  const limiteUtilizadoRotativo = limitePrincipal - saldoDisponivel;
  const limiteUtilizadoRotativoComExtra =
    limiteExtraTotal - saldoDisponivelExtra;

  const chartDataRotativo = [
    {
      name: "DISPONÍVEL",
      value: utilizaLimiteExtra ? saldoDisponivelExtra : saldoDisponivel || 0,
    },
    {
      name: "UTILIZADO",
      value: utilizaLimiteExtra
        ? limiteUtilizadoRotativoComExtra
        : limiteUtilizadoRotativo,
    },
  ];

  const limiteParcelado = cardFinancial?.limiteParcelado ?? 0;
  const saldoDisponivelParcelado = cardFinancial?.saldoDisponivelParcelado ?? 0;
  const limiteUtilizadoParcelado = limiteParcelado - saldoDisponivelParcelado;

  const chartDataParcelado = [
    {
      name: "DISPONÍVEL",
      value: parseFloat(saldoDisponivelParcelado) || 0,
    },
    {
      name: "UTILIZADO",
      value: limiteUtilizadoParcelado > 0 ? limiteUtilizadoParcelado : 0,
    },
  ];

  const limiteSaque = cardFinancial?.limiteSaque ?? 0;
  const saldoDisponivelSaque = cardFinancial?.saldoDisponivelSaque ?? 0;
  const limiteUtilizadoSaque = limiteSaque - saldoDisponivelSaque;

  const chartDataSaque = [
    {
      name: "DISPONÍVEL",
      value: parseFloat(saldoDisponivelSaque) || 0,
    },
    {
      name: "UTILIZADO",
      value: limiteUtilizadoSaque > 0 ? limiteUtilizadoSaque : 0,
    },
  ];

  const isSaldoNegativo =
    (limiteExtra < 0 && saldoDisponivel < 0) ||
    (limiteExtra >= 0 && saldoDisponivelExtra < 0);

  const rotativeColors = [
    isSaldoNegativo
      ? designSystemColors.errorColor
      : designSystemColors.activateGreen,
    designSystemColors.cancelButtons,
  ];

  const COLORS = [
    designSystemColors.cancelButtons,
    designSystemColors.lightOrange,
  ];

  const outerRadii = {
    DISPONÍVEL: 80,
    UTILIZADO: 90,
  };

  const isLoading = isLoadingCardFinancial || (isLoadingClient && !cardFinancial);

  return (
    <>
      <S.WrapperCardLimit ref={ref}>
        {isLoading ? (
          <S.WrapperNewLoading>
            <LoadingElevaty />
          </S.WrapperNewLoading>
        ) : (
          <Card title="Limites do cliente" modalTitle="Limites do cliente">
            <Tabs
              tabs={[
                {
                  id: "tab-limite-rotativo",
                  title: "Rotativo",
                  content: (
                    <ResponsiveContainer
                      width="100%"
                      height={utilizaLimiteExtra ? 400 : 300}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PieChart width={218} height={218}>
                          <Pie
                            data={chartDataRotativo}
                            cx={90}
                            cy={90}
                            innerRadius={62}
                            startAngle={90}
                            endAngle={450}
                            paddingAngle={1}
                            dataKey="value"
                            labelLine={false}
                          >
                            {chartDataRotativo.map((entry, index) => (
                              <Cell
                                key={`cell-${entry.name}`}
                                fill={
                                  rotativeColors[index % rotativeColors.length]
                                }
                                stroke={
                                  rotativeColors[index % rotativeColors.length]
                                }
                                strokeWidth={entry.name === "UTILIZADO" ? 6 : 1}
                                outerRadius={outerRadii[entry.name]}
                              />
                            ))}
                            <Label
                              content={
                                <CustomLabel
                                  value={
                                    limiteExtra > 0
                                      ? limitePrincipal + limiteExtra
                                      : limitePrincipal
                                  }
                                />
                              }
                              position="center"
                            />
                          </Pie>
                        </PieChart>
                        <CustomLegend
                          payload={chartDataRotativo.map((entry, index) => ({
                            value: entry.name,
                            color:
                              rotativeColors[index % rotativeColors.length],
                            payload: entry,
                          }))}
                        />
                      </div>
                      <LimitInformationContainer
                        utilizaLimiteExtra={utilizaLimiteExtra}
                        devedorTotal={devedorTotal}
                        limiteExtra={limiteExtra}
                        limitePrincipal={limitePrincipal}
                        saldoDisponivel={saldoDisponivel}
                        saldoDisponivelExtra={saldoDisponivelExtra}
                        total={total}
                      />
                    </ResponsiveContainer>
                  ),
                },
                {
                  id: "tab-limite-parcelado",
                  title: "Parcelado",
                  content: (
                    <ResponsiveContainer width="100%" height={216}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PieChart width={218} height={218}>
                          <Pie
                            data={chartDataParcelado}
                            cx={90}
                            cy={90}
                            innerRadius={62}
                            startAngle={90}
                            endAngle={450}
                            paddingAngle={1}
                            dataKey="value"
                            labelLine={false}
                          >
                            {chartDataParcelado.map((entry, index) => (
                              <Cell
                                key={`cell-${entry.name}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={entry.name === "UTILIZADO" ? 6 : 1}
                                outerRadius={outerRadii[entry.name]}
                              />
                            ))}
                            <Label
                              content={
                                <CustomLabel
                                  value={limiteParcelado}
                                />
                              }
                              position="center"
                            />
                          </Pie>
                        </PieChart>
                        <CustomLegend
                          payload={chartDataParcelado.map((entry, index) => ({
                            value: entry.name,
                            color: COLORS[index % COLORS.length],
                            payload: entry,
                          }))}
                        />
                      </div>
                    </ResponsiveContainer>
                  ),
                },
                {
                  id: "tab-limite-saque",
                  title: "Saque",
                  content: (
                    <ResponsiveContainer width="100%" height={216}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PieChart width={218} height={218}>
                          <Pie
                            data={chartDataSaque}
                            cx={90}
                            cy={90}
                            innerRadius={62}
                            startAngle={90}
                            endAngle={450}
                            paddingAngle={1}
                            dataKey="value"
                            labelLine={false}
                          >
                            {chartDataSaque.map((entry, index) => (
                              <Cell
                                key={`cell-${entry.name}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={entry.name === "UTILIZADO" ? 6 : 1}
                                outerRadius={outerRadii[entry.name]}
                              />
                            ))}
                            <Label
                              content={
                                <CustomLabel
                                  value={limiteSaque}
                                />
                              }
                              position="center"
                            />
                          </Pie>
                        </PieChart>
                        <CustomLegend
                          payload={chartDataSaque.map((entry, index) => ({
                            value: entry.name,
                            color: COLORS[index % COLORS.length],
                            payload: entry,
                          }))}
                        />
                      </div>
                    </ResponsiveContainer>
                  ),
                },
              ].filter(Boolean)}
            />
          </Card>
        )}
      </S.WrapperCardLimit>
    </>
  );
}

export default memo(CardsLimitChart);